import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFinancialPlanStore } from '../store/financialPlanStore'
import { TrendingUp, Shield, Target, BarChart3, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react'

export function Landing() {
  const navigate = useNavigate()
  const createPlan = useFinancialPlanStore(state => state.createPlan)
  
  const [clientName, setClientName] = useState('')
  const [email, setEmail] = useState('')
  const [advisorCode, setAdvisorCode] = useState('')
  
  const handleStartPlan = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!clientName || !email) {
      alert('Please enter your name and email')
      return
    }
    
    createPlan(clientName, email, advisorCode || undefined)
    navigate('/intake')
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-neutral-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-primary-600 to-primary-800 rounded-xl shadow-lg">
                <TrendingUp className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-serif font-bold text-neutral-900">
                  Watts Wealth Management
                </h1>
                <p className="text-sm text-neutral-600 font-medium">Financial Planning Platform</p>
              </div>
            </div>
            <a
              href="https://www.wattswealthmanagement.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 font-semibold text-sm flex items-center gap-1 transition-colors"
            >
              Visit Main Site 
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100/80 rounded-full mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-primary-600" />
            <span className="text-sm font-semibold text-primary-700">Advanced Financial Planning Technology</span>
          </div>
          
          <h2 className="section-title text-5xl md:text-6xl mb-6 text-balance">
            Take Control of Your
            <span className="block bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              Financial Future
            </span>
          </h2>
          
          <p className="section-subtitle max-w-2xl mx-auto mb-12 text-balance">
            Experience advanced Monte Carlo simulations, comprehensive scenario planning, 
            and personalized wealth management strategies—all in one powerful platform.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 pb-16">
          {/* Features */}
          <div className="space-y-6">
            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary-100 rounded-xl">
                  <BarChart3 className="h-7 w-7 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-bold text-neutral-900 mb-2">
                    Monte Carlo Analysis
                  </h3>
                  <p className="text-neutral-600 leading-relaxed">
                    Run 1,000+ simulations to see your retirement success probability across 
                    different market scenarios. Make informed decisions with data-driven insights.
                  </p>
                </div>
              </div>
            </div>

            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-accent-100 rounded-xl">
                  <Target className="h-7 w-7 text-accent-600" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-bold text-neutral-900 mb-2">
                    What-If Scenarios
                  </h3>
                  <p className="text-neutral-600 leading-relaxed">
                    Explore different life events, market conditions, and planning changes to 
                    stress-test your financial plan before they happen.
                  </p>
                </div>
              </div>
            </div>

            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-success-100 rounded-xl">
                  <Shield className="h-7 w-7 text-success-600" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-bold text-neutral-900 mb-2">
                    Tax Optimization
                  </h3>
                  <p className="text-neutral-600 leading-relaxed">
                    Roth conversion strategies, withdrawal optimization, and tax-efficient 
                    planning for your retirement years.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl border border-neutral-200">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="h-5 w-5 text-primary-600" />
                <span className="font-semibold text-neutral-900">Secure & Private</span>
              </div>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Your data is stored securely in your browser. We never transmit your personal 
                financial information without your explicit consent.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="card-premium">
              <div className="mb-6">
                <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
                  Get Started with Your Financial Plan
                </h3>
                <p className="text-neutral-600">
                  Begin your journey to financial confidence in minutes.
                </p>
              </div>

              <form onSubmit={handleStartPlan} className="space-y-5">
                <div>
                  <label htmlFor="name" className="label">
                    Full Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="label">
                    Email Address <span className="text-danger-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="advisor" className="label">
                    Advisor Code <span className="text-neutral-400">(Optional)</span>
                  </label>
                  <input
                    id="advisor"
                    type="text"
                    placeholder="If you were provided a code by your advisor"
                    value={advisorCode}
                    onChange={(e) => setAdvisorCode(e.target.value)}
                    className="input-field"
                  />
                  <p className="text-xs text-neutral-500 mt-2">
                    Enter your advisor's unique code to connect your plan with their dashboard.
                  </p>
                </div>

                <button type="submit" className="btn-primary w-full text-lg flex items-center justify-center gap-2">
                  Start Your Financial Plan
                  <ArrowRight className="h-5 w-5" />
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-neutral-200">
                <p className="text-xs text-neutral-500 text-center leading-relaxed">
                  Your data is stored securely in your browser. We never transmit your personal 
                  financial information without your explicit consent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-300 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-sm mb-2">
              © {new Date().getFullYear()} Watts Wealth Management. All rights reserved.
            </p>
            <p className="text-xs text-neutral-400 max-w-2xl mx-auto">
              This tool provides projections for educational purposes only. Past performance 
              does not guarantee future results. Consult with a qualified financial advisor 
              before making investment decisions.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
